<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import {
  hasRooms,
  emptyTenant,
  tenantToForm,
  validateTenant,
  tenantPayload,
} from '../residentsForm';
import FormField from './FormField.vue';

export default {
  name: 'TenantModal',
  components: { BaseModal, FormField },
  props: {
    tenant: { type: Object, default: null },
    presetCondoId: { type: String, default: '' },
  },
  emits: ['close', 'saved'],
  data() {
    const form = this.tenant ? tenantToForm(this.tenant) : emptyTenant();
    if (!this.tenant && this.presetCondoId) form.condo_id = this.presetCondoId;
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      form,
      units: [],
      errors: {},
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    isEdit() {
      return !!this.tenant;
    },
    selectedUnit() {
      return this.units.find(unit => unit.id === this.form.unit_id);
    },
    needsRoom() {
      return !!this.selectedUnit && hasRooms(this.selectedUnit.kind);
    },
    // The configured languages, plus the tenant's own if it was removed since.
    languages() {
      const list = [...((this.settings && this.settings.languages) || [])];
      if (this.form.language && !list.includes(this.form.language)) {
        list.push(this.form.language);
      }
      return list;
    },
  },
  watch: {
    'form.condo_id': {
      immediate: true,
      handler(condoId, previous) {
        if (previous !== undefined) {
          this.form.unit_id = '';
          this.form.room_id = '';
        }
        this.loadUnits(condoId);
      },
    },
    'form.unit_id'(unitId, previous) {
      if (previous) this.form.room_id = '';
    },
  },
  methods: {
    ...mapActions('coLivingResidents', [
      'fetchAllUnits',
      'addTenant',
      'editTenant',
    ]),
    async loadUnits(condoId) {
      this.units = [];
      if (!condoId) return;
      try {
        this.units = await this.fetchAllUnits(condoId);
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.COMMON.LOAD_FAILED'));
      }
    },
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.RESIDENTS.ERRORS.${this.errors[field]}`)
        : '';
    },
    showServerError(err) {
      const messages = {
        phone_taken: ['phone', 'PHONE_TAKEN'],
        invalid_phone: ['phone', 'PHONE'],
        invalid_location: ['unit_id', 'INVALID_LOCATION'],
        unit_full: ['unit_id', 'UNIT_FULL'],
      };
      const hit = messages[apiErrorCode(err)];
      if (hit) {
        this.errors = { [hit[0]]: hit[1] };
      } else {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.RESIDENTS.SAVE_FAILED'));
      }
    },
    async onSubmit() {
      this.errors = validateTenant(this.form, this.selectedUnit);
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const fields = tenantPayload(this.form, this.selectedUnit);
      try {
        if (this.isEdit) {
          await this.editTenant({ id: this.tenant.id, fields });
        } else {
          await this.addTenant(fields);
        }
        useAlert(this.$t('CO_LIVING.RESIDENTS.SAVED'));
        this.$emit('saved');
        this.$emit('close');
      } catch (err) {
        this.showServerError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.RESIDENTS.EDIT_TENANT')
          : $t('CO_LIVING.RESIDENTS.ADD_TENANT')
      }}
    </template>

    <form
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="co-living-tenant-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.NAME')"
        for-id="co-living-tenant-name"
        :error="errorText('name')"
      >
        <input
          id="co-living-tenant-name"
          v-model="form.name"
          type="text"
          maxlength="120"
          :class="[fieldClass, errors.name ? 'border-red-500' : '']"
          data-testid="co-living-tenant-name"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.PHONE')"
        for-id="co-living-tenant-phone"
        :error="errorText('phone')"
        :hint="$t('CO_LIVING.RESIDENTS.FIELDS.PHONE_HINT')"
      >
        <input
          id="co-living-tenant-phone"
          v-model="form.phone"
          type="tel"
          maxlength="32"
          :class="[fieldClass, errors.phone ? 'border-red-500' : '']"
          data-testid="co-living-tenant-phone"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.CONDO')"
        for-id="co-living-tenant-condo"
        :error="errorText('condo_id')"
      >
        <select
          id="co-living-tenant-condo"
          v-model="form.condo_id"
          :class="[selectClass, errors.condo_id ? 'border-red-500' : '']"
          data-testid="co-living-tenant-condo"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.RESIDENTS.FIELDS.CONDO_PLACEHOLDER') }}
          </option>
          <option v-for="condo in condos" :key="condo.id" :value="condo.id">
            {{ condo.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.UNIT')"
        for-id="co-living-tenant-unit"
        :error="errorText('unit_id')"
        :hint="$t('CO_LIVING.RESIDENTS.FIELDS.UNIT_HINT')"
      >
        <select
          id="co-living-tenant-unit"
          v-model="form.unit_id"
          :disabled="!form.condo_id"
          :class="[selectClass, errors.unit_id ? 'border-red-500' : '']"
          data-testid="co-living-tenant-unit"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.RESIDENTS.FIELDS.UNIT_PLACEHOLDER') }}
          </option>
          <option v-for="unit in units" :key="unit.id" :value="unit.id">
            {{ unit.name }}
          </option>
        </select>
      </FormField>

      <FormField
        v-if="needsRoom"
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.ROOM')"
        for-id="co-living-tenant-room"
        :error="errorText('room_id')"
      >
        <select
          id="co-living-tenant-room"
          v-model="form.room_id"
          :class="[selectClass, errors.room_id ? 'border-red-500' : '']"
          data-testid="co-living-tenant-room"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.RESIDENTS.FIELDS.ROOM_PLACEHOLDER') }}
          </option>
          <option
            v-for="room in selectedUnit.rooms"
            :key="room.id"
            :value="room.id"
          >
            {{ room.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.RESIDENTS.FIELDS.LANGUAGE')"
        for-id="co-living-tenant-language"
      >
        <select
          id="co-living-tenant-language"
          v-model="form.language"
          :class="selectClass"
          data-testid="co-living-tenant-language"
        >
          <option value="">{{ $t('CO_LIVING.COMMON.NONE') }}</option>
          <option
            v-for="language in languages"
            :key="language"
            :value="language"
          >
            {{ language }}
          </option>
        </select>
      </FormField>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.active"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-tenant-active"
        />
        {{ $t('CO_LIVING.COMMON.ACTIVE') }}
      </label>

      <div class="md:col-span-2 flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-tenant-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
